import { memo, useCallback, useId, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Checkbox } from '@m/alchemy-ui/Checkbox';
import { CheckboxGroup, type CheckboxGroupProps } from '@m/alchemy-ui/CheckboxGroup';
import { Select } from '@m/alchemy-ui/Select';
import { LeaderboardTypeEnum } from '../../../../common/core-api';
import { type PanelConfigPatch } from '../../common/panel-grid';
import { type PageCmsPanel } from '../../common/panel-types';
import { PanelEditorLabel, PanelEditorSection } from '../../CustomPage.styled';
import { panelEditorMessages } from './panel-editor-messages';

const messages = defineMessages({
  leaderboard: {
    id: 'leaderboard',
    defaultMessage: 'Leaderboard'
  },
  newbies: {
    id: 'newbies',
    defaultMessage: 'Newbies'
  }
});

const LEADERBOARD_ORDER = [LeaderboardTypeEnum.leaderboard, LeaderboardTypeEnum.newbies];

type LeaderboardConfig = Extract<PageCmsPanel['config'], { __typename: 'LeaderboardPanelConfig' }>;

interface LeaderboardPanelEditorProps {
  readonly config: LeaderboardConfig;
  readonly onConfigChange: (patch: PanelConfigPatch) => void;
}

const LeaderboardPanelEditorComponent = ({ config, onConfigChange }: LeaderboardPanelEditorProps) => {
  const intl = useIntl();
  const id = useId();
  const displayLabelId = `leaderboard-display-label-${id}`;
  const defaultLabelId = `leaderboard-default-label-${id}`;
  const { leaderboardDisplay, leaderboardDefault } = config;

  const labels: Record<LeaderboardTypeEnum, string> = useMemo(
    () => ({
      [LeaderboardTypeEnum.leaderboard]: intl.formatMessage(messages.leaderboard),
      [LeaderboardTypeEnum.newbies]: intl.formatMessage(messages.newbies)
    }),
    [intl]
  );

  // Re-derived in canonical order as a new array — the config arrays are frozen Apollo data.
  const checked = useMemo(
    () => LEADERBOARD_ORDER.filter((type) => leaderboardDisplay.includes(type)),
    [leaderboardDisplay]
  );
  const isDefaultEnabled = checked.length > 1;

  // Derive the displayed default from the checked options rather than trusting the stored
  // default — legacy data can hold e.g. display=['newbies'] with default='leaderboard'.
  const displayedDefault = checked.includes(leaderboardDefault) ? leaderboardDefault : (checked[0] ?? '');

  const defaultOptions = useMemo(
    () => checked.map((type) => ({ value: type as string, label: labels[type] })),
    [checked, labels]
  );

  const handleDisplayChange = useCallback<NonNullable<CheckboxGroupProps['onChange']>>(
    (selection) => {
      const nextDisplay = LEADERBOARD_ORDER.filter((type) => selection.includes(type));
      onConfigChange({
        leaderboardDisplay: nextDisplay,
        // Keep the stored default within the checked options; when nothing is checked the
        // stored value stays as-is (the field is non-nullable) and the select shows empty.
        ...(nextDisplay.length > 0 && !nextDisplay.includes(leaderboardDefault)
          ? { leaderboardDefault: nextDisplay[0] }
          : {})
      });
    },
    [leaderboardDefault, onConfigChange]
  );

  const handleDefaultChange = useCallback(
    (option: { value: string }) => onConfigChange({ leaderboardDefault: option.value as LeaderboardTypeEnum }),
    [onConfigChange]
  );

  return (
    <>
      <PanelEditorSection>
        <PanelEditorLabel id={displayLabelId}>{intl.formatMessage(panelEditorMessages.display)}</PanelEditorLabel>
        <CheckboxGroup selection={checked} onChange={handleDisplayChange}>
          {({ isSelected, handleChange }) =>
            LEADERBOARD_ORDER.map((type) => (
              <Checkbox
                key={type}
                label={labels[type]}
                checked={isSelected(type)}
                onChange={() => handleChange(type)}
              />
            ))
          }
        </CheckboxGroup>
      </PanelEditorSection>
      <PanelEditorSection>
        <PanelEditorLabel id={defaultLabelId}>{intl.formatMessage(panelEditorMessages.default)}</PanelEditorLabel>
        <Select
          labelId={defaultLabelId}
          isFullWidth
          disabled={!isDefaultEnabled}
          value={displayedDefault}
          options={defaultOptions}
          onChange={handleDefaultChange}
        />
      </PanelEditorSection>
    </>
  );
};

// Immutable config updates make prop comparison cheap: the editor re-renders only when its
// own config (or the grid callback) changes.
export const LeaderboardPanelEditor = memo(LeaderboardPanelEditorComponent);
